import {Separator} from 'platform/components';
import {Box, Hide, Show, VStack} from 'platform/foundation';

import {ReactNode, useState} from 'react';

import {mergeRecordAuditTasks, RecordAuditResponseBody} from '@dms/api/core';

import {RecordAuditScorableResult} from './computeRecordAuditScore';
import {RecordAuditForm} from './RecordAuditForm';
import {RecordAuditScoreBar, RecordAuditScoreFilter} from './RecordAuditScoreBar';

// Bottom padding reserved on mobile/tablet so the fixed bottom score bar never covers the last task.
const BOTTOM_BAR_CLEARANCE = 40;

interface RecordAuditFillPanelProps {
  audit: RecordAuditResponseBody;
  isEditable: boolean;
  /**
   * `card` (default) draws the panel's own card chrome (border + white background + padding) —
   * the audit detail page layout. `flat` drops that chrome for hosts already inside a card
   * (e.g. the service order Audits tab): the score bar becomes a self-bordered toolbar and the
   * task list renders bare, so the host card's chrome isn't duplicated.
   */
  variant?: 'card' | 'flat';
  /**
   * Optional node rendered inside the card, above the score bar. Consumers that want a
   * title (e.g. the audit detail page) pass it here; the plain fill experience omits it.
   */
  header?: ReactNode;
}

/**
 * Reusable record-audit fill experience: the completion/score bar plus the OK/BAD/NA
 * task checklist. Self-contained — it recomputes tasks and live results from `audit`
 * and drives every result submit through its own RTK mutations. Deliberately carries
 * no entity header or participants; those stay with the audit detail page.
 */
export function RecordAuditFillPanel(props: RecordAuditFillPanelProps) {
  const [activeFilter, setActiveFilter] = useState<RecordAuditScoreFilter>('all');

  const tasks = mergeRecordAuditTasks(props.audit);
  const liveResults: RecordAuditScorableResult[] = tasks.map((task) => ({
    weight: task.weight,
    value: task.value,
  }));

  const isFlat = props.variant === 'flat';

  return (
    <>
      <Box
        backgroundColor={isFlat ? undefined : 'palettes.white.10.100'}
        border={isFlat ? undefined : '1px solid'}
        borderColor={isFlat ? undefined : 'palettes.neutral.40.100'}
        borderRadius={isFlat ? undefined : 'medium'}
      >
        <VStack spacing={0}>
          <Show when={props.header != null}>
            <Box padding={isFlat ? undefined : 4} paddingBottom={isFlat ? 4 : undefined}>
              {props.header}
            </Box>
            {/* Mobile/tablet: the score bar is absent here (it lives fixed at the bottom), so a
                divider bridges the header to the sections, mirroring the desktop score bar's border. */}
            <Show onMobile onTablet>
              <Separator spacing={0} />
            </Show>
          </Show>

          {/* Desktop/notebook: the score bar sticks inline at the top of the card. On mobile/tablet
            the app's back-navigation bar hides on scroll and would leave content bleeding above a
            sticky bar, so there it is rendered as a fixed bottom bar instead (see below). */}
          <Hide onMobile onTablet>
            {/* Flat mode has no surrounding card border, so the bar carries its own full border
                and radius and reads as a standalone toolbar. */}
            <Box
              position="sticky"
              top={0}
              zIndex="CONTENT"
              backgroundColor="palettes.neutral.20.100"
              border={isFlat ? '1px solid' : undefined}
              borderRadius={isFlat ? 'small' : undefined}
              borderTop={!isFlat && props.header != null ? '1px solid' : undefined}
              borderBottom={isFlat ? undefined : '1px solid'}
              borderColor="palettes.neutral.40.100"
              paddingHorizontal={4}
              paddingVertical={4}
            >
              <RecordAuditScoreBar
                audit={props.audit}
                liveResults={liveResults}
                activeFilter={activeFilter}
                onFilterChange={setActiveFilter}
              />
            </Box>
          </Hide>

          {/* Flat: no horizontal inset (the host card pads), and the top gap below the inline
              score bar only exists where that bar renders (notebook/desktop). */}
          <Box padding={isFlat ? undefined : 4} paddingTop={isFlat ? [0, 0, 4, 4] : undefined}>
            <RecordAuditForm
              audit={props.audit}
              tasks={tasks}
              isDisabled={!props.isEditable}
              activeFilter={activeFilter}
            />
            {/* Clearance so the fixed bottom bar never covers the last task on mobile/tablet. */}
            <Show onMobile onTablet>
              <Box height={BOTTOM_BAR_CLEARANCE} />
            </Show>
          </Box>
        </VStack>
      </Box>
      {/* Mobile/tablet: the score bar lives as an always-visible bar fixed to the bottom of the
          viewport, sidestepping the app's scroll-hiding top navigation bar entirely. */}
      <Show onMobile onTablet>
        <Box
          position="fixed"
          bottom={0}
          left={0}
          right={0}
          zIndex="PAGE_NAVIGATION"
          backgroundColor="palettes.neutral.20.100"
          borderTop="1px solid"
          borderColor="palettes.neutral.40.100"
          paddingHorizontal={4}
          paddingVertical={3}
        >
          <RecordAuditScoreBar
            audit={props.audit}
            liveResults={liveResults}
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
          />
        </Box>
      </Show>
    </>
  );
}
